/**
 * Settlements Page
 *
 * 정산 이력 페이지
 * - 수익 공유 정산 내역
 * - 정산 상태별 필터링 (완료/대기)
 * - 총 수익 공유 금액 표시
 * - 정산 기간 및 수익 상세
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Table, Select, Tag, Spin, Button, Card, Row, Col, Statistic } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';

import { getMySettlements } from '../../api/copyTrading';
// import { getMyEarnings, getEarningsSummary } from '../../api/copyTrading.real'; // TODO: Implement these functions
const getMyEarnings = async (_params?: any) => ({ success: true, data: { data: [], pagination: null } } as any);
const getEarningsSummary = async () => ({
  success: true,
  data: {
    totalEarned: 0,
    thisWeekEarned: 0,
    thisMonthEarned: 0,
    pendingAmount: 0,
    completedCount: 0,
    pendingCount: 0
  }
} as any);
import type { CopySettlement } from '../../../types/copyTrading';

const { Option } = Select;

export function SettlementsPage() {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);

  // ==========================================
  // State
  // ==========================================

  // 역할 뷰: paid (팔로워가 지불), earned (리더가 받음)
  const [roleView, setRoleView] = useState<'paid' | 'earned'>('paid');

  const [settlements, setSettlements] = useState<CopySettlement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalProfitShared, setTotalProfitShared] = useState(0);

  // Earned 요약 정보
  const [earnedSummary, setEarnedSummary] = useState({
    totalEarned: 0,
    thisWeekEarned: 0,
    thisMonthEarned: 0,
    pendingAmount: 0,
  });

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending'>('all');

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    if (roleView === 'paid') {
      fetchPaidSettlements();
    } else {
      fetchEarnedSettlements();
      fetchEarnedSummary();
    }
  }, [roleView]);

  // ==========================================
  // API Calls
  // ==========================================

  const fetchPaidSettlements = async () => {
    setIsLoading(true);

    try {
      const response = await getMySettlements();

      if (response.success) {
        setSettlements(response.data.settlements);
        setTotalProfitShared(response.data.totalProfitShared);
      }
    } catch (error) {
      console.error('[SettlementsPage] fetchPaidSettlements error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEarnedSettlements = async () => {
    setIsLoading(true);

    try {
      const response = await getMyEarnings({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });

      if (response.success) {
        setSettlements(response.data.data);
      }
    } catch (error) {
      console.error('[SettlementsPage] fetchEarnedSettlements error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEarnedSummary = async () => {
    try {
      const response = await getEarningsSummary();
      if (response.success) {
        setEarnedSummary({
          totalEarned: response.data.totalEarned,
          thisWeekEarned: response.data.thisWeekEarned,
          thisMonthEarned: response.data.thisMonthEarned,
          pendingAmount: response.data.pendingAmount,
        });
      }
    } catch (error) {
      console.error('[SettlementsPage] fetchEarnedSummary error:', error);
    }
  };

  // ==========================================
  // Handlers
  // ==========================================

  const handleRefresh = () => {
    if (roleView === 'paid') {
      fetchPaidSettlements();
    } else {
      fetchEarnedSettlements();
      fetchEarnedSummary();
    }
  };

  const handleRoleToggle = (newRole: 'paid' | 'earned') => {
    setRoleView(newRole);
    setStatusFilter('all'); // 역할 변경 시 필터 초기화
  };

  // ==========================================
  // Filtering
  // ==========================================

  const filteredSettlements = settlements.filter((settlement) => {
    if (statusFilter === 'all') return true;
    return settlement.status === statusFilter;
  });

  // 통계 계산
  const completedCount = settlements.filter((s) => s.status === 'completed').length;
  const pendingCount = settlements.filter((s) => s.status === 'pending').length;
  const totalProfit = settlements.reduce(
    (sum, s) => getBigNumber(sum).plus(s.totalProfit).toNumber(),
    0
  );

  // ==========================================
  // Table Columns (역할에 따라 다르게 표시)
  // ==========================================

  const paidColumns: ColumnsType<CopySettlement> = useMemo(() => [
    {
      title: t('settlementPeriod'),
      key: 'period',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {dayjs(record.settlementPeriodStart).format('YYYY-MM-DD')}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            ~ {dayjs(record.settlementPeriodEnd).format('YYYY-MM-DD')}
          </div>
        </div>
      ),
    },
    {
      title: t('portfolioId'),
      dataIndex: 'portfolioId',
      key: 'portfolioId',
      width: 120,
      render: (portfolioId) => <strong>#{portfolioId}</strong>,
    },
    {
      title: t('totalProfit'),
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      width: 150,
      align: 'right',
      render: (totalProfit) => {
        const isPositive = totalProfit >= 0;
        return (
          <span
            style={{
              color: isPositive ? '#22c55e' : '#ef4444',
              fontWeight: 600,
            }}
          >
            {isPositive ? '+' : ''}
            {formatDecimal(totalProfit, 2)} USDT
          </span>
        );
      },
    },
    {
      title: t('profitShareRate'),
      dataIndex: 'profitShareRate',
      key: 'profitShareRate',
      width: 120,
      align: 'center',
      render: (profitShareRate) => (
        <Tag color="blue">{formatDecimal(profitShareRate, 2)}%</Tag>
      ),
    },
    {
      title: t('profitShareAmount'),
      dataIndex: 'profitShareAmount',
      key: 'profitShareAmount',
      width: 150,
      align: 'right',
      render: (profitShareAmount) => (
        <strong style={{ color: '#ef4444' }}>
          -{formatDecimal(profitShareAmount, 2)} USDT
        </strong>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      fixed: 'right',
      render: (status: 'completed' | 'pending') => {
        const statusConfig = {
          completed: { text: t('completed'), color: 'green' },
          pending: { text: t('pending'), color: 'orange' },
        };
        const config = statusConfig[status];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: t('settlementDate'),
      dataIndex: 'settledAt',
      key: 'settledAt',
      width: 150,
      render: (settledAt) => {
        if (!settledAt) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <div>
            <div>{dayjs(settledAt).format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {dayjs(settledAt).format('HH:mm:ss')}
            </div>
          </div>
        );
      },
    },
  ], [t]);

  const earnedColumns: ColumnsType<CopySettlement> = useMemo(() => [
    {
      title: t('settlementPeriod'),
      key: 'period',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {dayjs(record.settlementPeriodStart).format('YYYY-MM-DD')}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            ~ {dayjs(record.settlementPeriodEnd).format('YYYY-MM-DD')}
          </div>
        </div>
      ),
    },
    {
      title: t('followerProfit'),
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      width: 150,
      align: 'right',
      render: (totalProfit) => {
        const isPositive = totalProfit >= 0;
        return (
          <span
            style={{
              color: isPositive ? '#22c55e' : '#ef4444',
              fontWeight: 600,
            }}
          >
            {isPositive ? '+' : ''}
            {formatDecimal(totalProfit, 2)} USDT
          </span>
        );
      },
    },
    {
      title: t('profitShareRate'),
      dataIndex: 'profitShareRate',
      key: 'profitShareRate',
      width: 120,
      align: 'center',
      render: (profitShareRate) => (
        <Tag color="blue">{formatDecimal(profitShareRate, 2)}%</Tag>
      ),
    },
    {
      title: t('receivedAmount'),
      dataIndex: 'profitShareAmount',
      key: 'profitShareAmount',
      width: 150,
      align: 'right',
      render: (profitShareAmount) => (
        <strong style={{ color: '#22c55e', fontSize: 16 }}>
          +{formatDecimal(profitShareAmount, 2)} USDT
        </strong>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      fixed: 'right',
      render: (status: 'completed' | 'pending') => {
        const statusConfig = {
          completed: { text: t('completed'), color: 'green' },
          pending: { text: t('pending'), color: 'orange' },
        };
        const config = statusConfig[status];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: t('settlementDate'),
      dataIndex: 'settledAt',
      key: 'settledAt',
      width: 150,
      render: (settledAt) => {
        if (!settledAt) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <div>
            <div>{dayjs(settledAt).format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {dayjs(settledAt).format('HH:mm:ss')}
            </div>
          </div>
        );
      },
    },
  ], [t]);

  // ==========================================
  // Render
  // ==========================================

  return (
    <>
      <PageWrapper>
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitle>{t('settlementsHistory')}</HeaderTitle>
          <HeaderDescription>{t('settlementsDesc')}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <RoleToggle>
              <RoleButton
                active={roleView === 'paid'}
                onClick={() => handleRoleToggle('paid')}
                isDark={isDarkMode}
                type="paid"
              >
                {t('profitPaid')}
              </RoleButton>
              <RoleButton
                active={roleView === 'earned'}
                onClick={() => handleRoleToggle('earned')}
                isDark={isDarkMode}
                type="earned"
              >
                {t('profitEarned')}
              </RoleButton>
            </RoleToggle>
          </StatItem>
          <StatDivider />
          <StatItem>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('refresh')}
            </Button>
          </StatItem>
        </StatsRow>
      </CompactHeader>

      {/* 요약 카드 - 컴팩트한 인라인 통계 */}
      <InlineStatsRow isDark={isDarkMode}>
        {roleView === 'paid' ? (
          <>
            <InlineStatCard>
              <StatIcon style={{ color: '#ef4444' }}><DollarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>총 지불 금액</InlineStatLabel>
                <InlineStatValue style={{ color: '#ef4444' }}>
                  {formatDecimal(totalProfitShared, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon style={{ color: totalProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                <DollarOutlined />
              </StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>총 수익</InlineStatLabel>
                <InlineStatValue style={{ color: totalProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                  {formatDecimal(totalProfit, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon><CalendarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>완료된 정산</InlineStatLabel>
                <InlineStatValue>{completedCount} 건</InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon style={{ color: '#f59e0b' }}><CalendarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>대기 중 정산</InlineStatLabel>
                <InlineStatValue style={{ color: '#f59e0b' }}>{pendingCount} 건</InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>
          </>
        ) : (
          <>
            <InlineStatCard>
              <StatIcon style={{ color: '#22c55e' }}><DollarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>총 수익</InlineStatLabel>
                <InlineStatValue style={{ color: '#22c55e' }}>
                  {formatDecimal(earnedSummary.totalEarned, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon style={{ color: '#22c55e' }}><DollarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>이번 주 수익</InlineStatLabel>
                <InlineStatValue style={{ color: '#22c55e' }}>
                  {formatDecimal(earnedSummary.thisWeekEarned, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon style={{ color: '#22c55e' }}><DollarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>이번 달 수익</InlineStatLabel>
                <InlineStatValue style={{ color: '#22c55e' }}>
                  {formatDecimal(earnedSummary.thisMonthEarned, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>

            <InlineStatCard>
              <StatIcon style={{ color: '#f59e0b' }}><CalendarOutlined /></StatIcon>
              <InlineStatInfo>
                <InlineStatLabel>대기 중</InlineStatLabel>
                <InlineStatValue style={{ color: '#f59e0b' }}>
                  {formatDecimal(earnedSummary.pendingAmount, 2)} USDT
                </InlineStatValue>
              </InlineStatInfo>
            </InlineStatCard>
          </>
        )}
      </InlineStatsRow>

      {/* 필터 섹션 */}
      <FilterSection isDark={isDarkMode}>
        <FilterRow>
          <FilterItem>
            <FilterLabel>상태</FilterLabel>
            <Select
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">전체</Option>
              <Option value="completed">완료</Option>
              <Option value="pending">대기</Option>
            </Select>
          </FilterItem>
        </FilterRow>
      </FilterSection>

      {/* 테이블 (역할에 따라 다른 컬럼) */}
      <TableSection isDark={isDarkMode}>
        <Table
          columns={roleView === 'paid' ? paidColumns : earnedColumns}
          dataSource={filteredSettlements}
          rowKey={(record) => record.id}
          loading={isLoading}
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `총 ${total}개`,
            pageSizeOptions: ['10', '20', '50'],
          }}
        />
      </TableSection>

      {/* 안내 문구 */}
      {!isLoading && filteredSettlements.length === 0 && (
        <EmptyState isDark={isDarkMode}>
          <EmptyIcon>💰</EmptyIcon>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No settlements yet</p>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            Settlements are processed automatically every week when profits are generated
          </p>
        </EmptyState>
      )}
      </PageWrapper>
    </>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  width: 1200px;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RoleToggle = styled.div`
  display: flex;
  gap: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const RoleButton = styled.button<{
  active: boolean;
  isDark: boolean;
  type: 'paid' | 'earned';
}>`
  padding: 6px 16px;
  border: none;
  background: ${({ active, isDark, type }) => {
    if (!active) return 'transparent';
    return type === 'paid' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)';
  }};
  color: ${({ active, type, isDark }) => {
    if (!active) return isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
    return type === 'paid' ? '#ef4444' : '#22c55e';
  }};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ type }) =>
      type === 'paid'
        ? 'rgba(239, 68, 68, 0.1)'
        : 'rgba(34, 197, 94, 0.1)'
    };
  }
`;

const InlineStatsRow = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  box-sizing: border-box;
  background: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const InlineStatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 20px;
  border-radius: 8px;
  background: rgba(255, 145, 49, 0.1);
  color: ${({ theme }) => theme.text};
`;

const InlineStatInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InlineStatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const InlineStatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const FilterSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 24px;
  padding: 20px;
  box-sizing: border-box;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const TableSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
  margin-bottom: 24px;
`;

const EmptyState = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 24px;
  padding: 80px 20px;
  box-sizing: border-box;
  text-align: center;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  color: ${({ theme }) => theme.text};

  p {
    margin: 8px 0;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;
